import React, { useState, useRef } from "react";
import { Box, Button, Chip, styled } from "@mui/material";
import ReactQuill from "react-quill";
import useQuillAccessibility from "utils/quillAccessibility";
import "react-quill/dist/quill.snow.css";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import notifiers from "global/constants/NotificationConstants";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
  setFileName,
} from "helpers/methods";
import {
  doDeleteTicketAttachment,
  uploadSupportTicketAttachment,
} from "screens/Tickets/CreateTicket/CreateTicketService";
import viewTicketTokens from "../ViewTicket.tokens";
import {
  attachIconSx,
  attachTriggerSx,
  cancelButtonSx,
  editorActionsSx,
  editorFooterSx,
  editorShellSx,
  saveButtonSx,
  stagedFilesSx,
} from "../ViewTicket.styles";
import { PaperclipIcon } from "./ViewTicketIcons";

const { editor } = viewTicketTokens;

/**
 * Renders a stored comment / description back into React elements. Ticket text
 * is authored in this editor and persisted as HTML.
 *
 * Also used by the notifications drawer, which stores the same kind of markup.
 */
export const parseHTML = (htmlString: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const rootElement = doc.body;

  const convertNode = (node: any, index: number = 0) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue;
    }

    const children: any = Array.from(node.childNodes).map(convertNode);

    const attributes: { [key: string]: string } = {};
    if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.attributes).forEach((attr: any) => {
        attributes[attr.name] = attr.value;
      });
    }

    const tagName = node.tagName.toLowerCase();

    // Handle IMG tag separately
    if (tagName === "img") {
      return React.createElement("img", {
        key: node.src,
        // Rich-text pasted from elsewhere rarely carries alt text; without one a
        // screen reader falls back to reading the URL (WCAG 1.1.1). Treat an
        // undescribed image as decorative instead.
        alt: "",
        ...attributes,
      });
    }

    // The parsed root is a <body>, which must not be re-rendered inside the
    // page (it produced a nested <body> element in the live DOM). A <div>
    // carries the same wrapper padding and is valid wherever this is used.
    return React.createElement(
      tagName === "body" ? "div" : tagName,
      {
        // Sibling elements of the same type previously collided on a
        // tag-name-only key, letting React reuse the wrong DOM node.
        key: `${tagName}-${index}`,
        ...attributes,
        style: { margin: 0, padding: 2 },
      },
      children.length ? children : null,
    );
  };

  return convertNode(rootElement);
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "link",
];

/** The four groups the design draws: headings, marks, lists, link. */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const ACCEPTED_FILE_TYPES =
  ".jpeg, .jpg, .png, .gif, .bmp, .tiff, .pdf, .doc, .docx, .txt, .xls, .xlsx, .heic";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

interface CustomProps {
  content: string;
  onChange: (content: string) => void;
  handleCancel: Function;
  handleAddComment: Function;
  ticketId: string;
  files: any[];
  setFiles: Function;
  /** Set on the always-open editor of a screen that has nothing to cancel to. */
  cancelBtn?: boolean;
  /**
   * Accessible name for the rich-text area. The design shows no visible label,
   * so the editor is named here (WCAG 4.1.2); replies pass "Reply" so the two
   * editors are told apart.
   */
  editorLabel?: string;
  isSaving?: boolean;
}

/**
 * Comment composer: the rich-text toolbar and body inside one outlined box,
 * closed by the attach hint and the Save / Cancel pair. Used unchanged for a
 * new comment and for a reply.
 */
const CommentEditor = (props: CustomProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const quillRef = useRef<ReactQuill>(null);

  useQuillAccessibility(quillRef, {
    label: props.editorLabel ?? "Comment",
    toolbarLabel: `${props.editorLabel ?? "Comment"} formatting`,
  });

  const handleUploadDocument = async (event: any) => {
    event.preventDefault();
    const selected: File[] = Array.from(event.target.files ?? []);
    // Clear the input so re-picking the same file still fires a change event.
    event.target.value = "";
    if (!selected.length) return;

    const tooLarge = selected.filter(
      (file) => file.size > editor.maxFileSizeBytes,
    );
    if (tooLarge.length) {
      openErrorNotification(
        `The following files exceed the maximum allowed size of 2 MB: ${tooLarge
          .map((file) => file.name)
          .join(", ")}`,
      );
    }

    const withinLimit = selected.filter(
      (file) => file.size <= editor.maxFileSizeBytes,
    );
    if (!withinLimit.length) return;

    try {
      setIsLoading(true);
      for (const element of withinLimit) {
        const fileDetails = new FormData();
        fileDetails.append("file", element);
        fileDetails.append("filename", element.name);
        const response = await uploadSupportTicketAttachment(
          fileDetails,
          props.ticketId,
        );
        if (response) {
          props.setFiles((prevFiles: any) => [...prevFiles, response.path]);
        }
      }
      // Announce completion (4.1.3): the loader is gone by now, so without this
      // a screen-reader user gets no confirmation the file attached — mirroring
      // the delete path, which already announces.
      if (withinLimit.length) {
        openSuccessNotification(
          withinLimit.length === 1
            ? `${withinLimit[0].name} attached`
            : `${withinLimit.length} files attached`,
        );
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  const handleDocumentDelete = async (path: string) => {
    try {
      setIsLoading(true);
      const response = await doDeleteTicketAttachment({ path });
      props.setFiles((prevFiles: any) =>
        prevFiles.filter((file: any) => file !== path),
      );
      openSuccessNotification(response.message);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  return (
    <>
      <Box sx={editorShellSx}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={props.content}
          onChange={props.onChange}
          formats={formats}
          modules={modules}
          placeholder="Write your comment"
        />

        {props.files?.length > 0 && (
          <Box sx={stagedFilesSx}>
            {props.files.map((file: string) => (
              <Chip
                key={file}
                size="small"
                label={setFileName(file)}
                // MUI makes the chip a focusable role="button" whose only action
                // is Delete/Backspace. Spelling that out is the only way a screen
                // reader user can discover it (WCAG 4.1.2); the visible file name
                // stays at the front of the name (2.5.3).
                aria-label={`${setFileName(file)}, attachment. Press Delete to remove.`}
                onDelete={() => handleDocumentDelete(file)}
              />
            ))}
          </Box>
        )}

        <Box sx={editorFooterSx}>
          <Button
            component="label"
            role={undefined}
            tabIndex={-1}
            disableRipple
            sx={attachTriggerSx}
          >
            <PaperclipIcon sx={attachIconSx} />
            Attach screenshots or files · {editor.maxFileSizeLabel}
            <VisuallyHiddenInput
              type="file"
              onChange={handleUploadDocument}
              accept={ACCEPTED_FILE_TYPES}
              multiple
            />
          </Button>

          <Box sx={editorActionsSx}>
            <Button
              variant="contained"
              disableElevation
              disabled={props.isSaving}
              onClick={() => props.handleAddComment()}
              sx={saveButtonSx}
            >
              Save
            </Button>
            {!props.cancelBtn && (
              <Button
                variant="outlined"
                disabled={props.isSaving}
                onClick={() => props.handleCancel()}
                sx={cancelButtonSx}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default CommentEditor;
